import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "../lib/config";
import { Button } from "../components/ui/button";
import { CheckCircle2, Loader2, XCircle, Home, Heart } from "lucide-react";
import { motion } from "framer-motion";

export default function SponsorshipSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [info, setInfo] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) return setStatus("error");
    let cancel = false;
    const MAX = 10;
    const poll = async (n) => {
      if (cancel) return;
      try {
        const { data } = await api.get(`/sponsorship/status/${sessionId}`);
        if (data.payment_status === "paid") {
          if (!cancel) {
            setInfo(data);
            setStatus("paid");
            launchHearts();
          }
          return;
        }
        if (data.status === "expired" || n >= MAX) {
          if (!cancel) setStatus("expired");
          return;
        }
        setAttempts(n + 1);
        setTimeout(() => poll(n + 1), 2000);
      } catch (e) {
        if (!cancel) setStatus("error");
      }
    };
    poll(0);
    return () => { cancel = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-orange-50 to-pink-50 flex items-center justify-center px-6 py-12">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-lg w-full bg-white rounded-2xl shadow-2xl overflow-hidden"
        data-testid="sponsor-success-card"
      >
        {status === "checking" && (
          <div className="p-12 text-center">
            <Loader2 className="w-16 h-16 text-rose-500 animate-spin mx-auto mb-6" />
            <h1 className="font-heading uppercase text-3xl text-black mb-2">Confirmando tu patrocinio</h1>
            <p className="text-gray-600">Un momento... ({attempts}/10)</p>
          </div>
        )}

        {status === "paid" && info && (
          <>
            <div className="bg-gradient-to-br from-rose-500 to-pink-600 p-8 text-center text-white">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring" }}
                className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center mx-auto mb-4"
              >
                <Heart className="w-12 h-12 text-white fill-white" />
              </motion.div>
              <h1 className="font-heading uppercase text-4xl mb-1">¡Gracias, {info.sponsor_name}!</h1>
              <p className="text-white/90 text-sm">Acabas de rescatar una vida</p>
            </div>
            <div className="p-8 text-center">
              <div className="font-heading text-6xl text-rose-600 mb-2">${info.amount}</div>
              <p className="text-gray-700 mb-6">
                {info.is_general ? (
                  <>Tu aporte fue al <strong>fondo general</strong>. La iglesia lo asignará a quien más lo necesite.</>
                ) : (
                  <>Tu aporte fue para <strong>{info.target_name}</strong>. {info.applied && "Su progreso se actualizó."}</>
                )}
              </p>
              {info.message && (
                <div className="p-4 bg-rose-50 border border-rose-100 rounded-xl text-sm text-gray-700 italic mb-6">
                  "{info.message}"
                </div>
              )}
              <Button onClick={() => navigate("/")} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="sponsor-success-home">
                <Home className="w-4 h-4 mr-2" /> Volver al inicio
              </Button>
            </div>
          </>
        )}

        {(status === "expired" || status === "error") && (
          <div className="p-12 text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
            <h1 className="font-heading uppercase text-3xl text-black mb-2">No pudimos confirmar el patrocinio</h1>
            <p className="text-gray-600 mb-6">Si el cobro fue exitoso, te llegará confirmación por email.</p>
            <Button onClick={() => navigate("/")} className="bg-rose-600 hover:bg-rose-700 text-white">Volver al inicio</Button>
          </div>
        )}
      </motion.div>
    </div>
  );
}

function launchHearts() {
  const colors = ["#F43F5E", "#EC4899", "#F97316", "#FBBF24"];
  for (let i = 0; i < 60; i++) {
    const el = document.createElement("div");
    el.className = "confetti";
    el.style.left = Math.random() * 100 + "vw";
    el.style.background = colors[Math.floor(Math.random() * colors.length)];
    el.style.borderRadius = "50%";
    el.style.width = "12px";
    el.style.height = "12px";
    el.style.animationDelay = Math.random() * 0.5 + "s";
    document.body.appendChild(el);
    setTimeout(() => el.remove(), 3500);
  }
}
